// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, CircularProgress, Paper, Alert } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AddScoreModal from "./AddScoreModal";
import { RootState, useAppDispatch, useAppSelector } from "@slices/store";
import { getInterviewById } from "@slices/interviewSlice/interview";
import { Interview } from "@slices/interviewSlice/model";
import { RequestState, Role } from "@utils/types";
import { InterviewStatus, SomethingWentWrong } from "@config/constant";
import { hasRequiredRole } from "@utils/utils";
import ErrorHandler from "@component/common/ErrorHandler";
import { useSnackbar } from "notistack";
import { SnackMessage } from "@config/constant";

const InterviewFeedback = () => {
  const { interviewId } = useParams<{ interviewId: string }>();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [clickedInterview, setClickedInterview] = useState<Interview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [hasFetchedInterview, setHasFetchedInterview] = useState(false);
  const [scoreFormModal, setScoreFormModal] = useState(false);

  const interviewRequestState = useAppSelector(
    (state: RootState) => state.interview.state
  );
  const selectedInterview = useAppSelector(
    (state: RootState) => state.interview.selectedInterview
  );
  const gettingSingleInterviewState = useAppSelector(
    (state: RootState) => state.interview.gettingSingleInterviewState
  );
  const userRoles = useAppSelector((state: RootState) => state.auth.roles);
  const userEmail = useAppSelector(
    (state: RootState) => state.auth.decodedIdToken?.email
  );

  useEffect(() => {
    if (!interviewId) {
      setError("Interview ID is missing");
      setLoading(false);
      return;
    }

    setClickedInterview(null);
    setError(null);
    setValidationError(null);
    setShowSuccessMessage(false);
    setHasFetchedInterview(false);
    setScoreFormModal(false);
    setLoading(true);
  }, [interviewId]);

  useEffect(() => {
    if (!interviewId) return;
    if (hasFetchedInterview) return;

    const fetchInterview = async () => {
      try {
        setLoading(true);
        setError(null);

        await dispatch(
          getInterviewById({
            Id: Number(interviewId),
          })
        ).unwrap();
      } catch (err: any) {
        if (err.code === "ERR_BAD_REQUEST") {
          setValidationError(
            "You do not have permission to submit feedback for this interview."
          );
        } else {
          const errorMessage = "Failed to fetch interview details";
          setError(errorMessage);
        }
      } finally {
        setHasFetchedInterview(true);
        setLoading(false);
      }
    };

    fetchInterview();
  }, [interviewId, dispatch, hasFetchedInterview]);

  useEffect(() => {
    if (!interviewId) return;
    if (!hasFetchedInterview) return;
    if (showSuccessMessage) return;
    if (error) return;
    if (validationError) return;

    if (!selectedInterview) {
      setError("Interview not found");
      return;
    }

    if (selectedInterview.status === InterviewStatus.COMPLETED) {
      setValidationError(
        "Feedback has already been submitted for this interview."
      );
      setLoading(false);
      return;
    }

    if (selectedInterview.status !== InterviewStatus.SCHEDULED) {
      setValidationError(
        "Cannot submit feedback because the interview is not scheduled."
      );
      setLoading(false);
      return;
    }

    setClickedInterview(selectedInterview);
    setError(null);
    setValidationError(null);
    setScoreFormModal(true);
    setLoading(false);
  }, [
    selectedInterview,
    interviewId,
    hasFetchedInterview,
    showSuccessMessage,
    error,
    validationError,
    userEmail,
    userRoles,
  ]);

  useEffect(() => {
    if (!scoreFormModal) return;

    if (interviewRequestState === RequestState.FAILED) {
      enqueueSnackbar(SnackMessage.error.addScore, {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
        preventDuplicate: true,
      });
    }
  }, [interviewRequestState, scoreFormModal, enqueueSnackbar]);

  const handleCloseScoreFormModal = () => {
    setScoreFormModal(false);
  };

  const handleSuccessRefresh = () => {
    setScoreFormModal(false);
    setShowSuccessMessage(true);
  };

  if (loading && !showSuccessMessage) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography>Loading interview details...</Typography>
      </Box>
    );
  }

  if (
    gettingSingleInterviewState === RequestState.FAILED &&
    !validationError &&
    !showSuccessMessage
  ) {
    return <ErrorHandler message={error || SomethingWentWrong} />;
  }

  if (showSuccessMessage) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          p: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{ p: 4, maxWidth: 500, textAlign: "center", borderRadius: 2 }}
        >
          <CheckCircleOutlineIcon
            sx={{ fontSize: 80, color: "success.main", mb: 2 }}
          />
          <Typography variant="h4" gutterBottom fontWeight="600">
            Thank You!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your feedback has been successfully submitted.
          </Typography>
          <Alert severity="success" icon={false} sx={{ mb: 3 }}>
            The interview feedback has been recorded.
          </Alert>
        </Paper>
      </Box>
    );
  }

  if (validationError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          p: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{ p: 4, maxWidth: 500, textAlign: "center", borderRadius: 2 }}
        >
          <ErrorOutlineIcon
            sx={{ fontSize: 80, color: "warning.main", mb: 2 }}
          />
          <Typography variant="h5" gutterBottom fontWeight="600">
            Access Restricted
          </Typography>
          <Alert
            severity="warning"
            icon={false}
            sx={{ mt: 2, mb: 3, textAlign: "center" }}
          >
            {validationError}
          </Alert>
        </Paper>
      </Box>
    );
  }

  if (error || !clickedInterview) {
    return <ErrorHandler message={error || SomethingWentWrong} />;
  }

  return (
    <AddScoreModal
      clickedInterview={clickedInterview}
      scoreFormModal={scoreFormModal}
      handleCloseScoreFormModal={handleCloseScoreFormModal}
      onSuccessRefresh={handleSuccessRefresh}
      hideCancelButton={true}
    />
  );
};

export default InterviewFeedback;
